import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { unixEpoch } from '@/utils/date-util'
import { User } from '@/features/iam/users/User'
import { computed, observable } from 'mobx'
import { once } from 'lodash-es'
import { asAtom, toStream } from '@/utils/mobx-util'
import { switchMap } from 'rxjs'
import { countdown } from '@/utils/rx-util'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { type TimeRange } from '@/infra/time-range'

/**
 * The action to be taken upon offer expiration: the opportunity is
 * automatically accepted or declined.
 */
export type OfferExpirationAction = 'Accept' | 'Decline'

export type OpportunityOfferProps = ModelProps<OpportunityOffer>

/**
 * An opportunity offer.
 */
export class OpportunityOffer extends Model<OpportunityOffer> {
  /**
   * An observable countdown. Ticks every second in sync with clock time until
   * the scheduled expiration time is reached.
   */
  private readonly _timeUntilExpiration$ = toStream(
    () => this.dateExpires,
    true,
  ).pipe(switchMap(countdown))

  /**
   * MobX-representation the time until expiration stream.
   */
  private readonly _timeUntilExpiration = asAtom(
    this._timeUntilExpiration$,
    null,
  )

  /**
   * The Opportunity ID.
   */
  id = Id.none

  /**
   * The workspace in which the opportunity is offered.
   */
  @observable accessor workspace = Workspace.none

  /**
   * The display name of the opportunity.
   */
  @observable accessor name = ''

  /**
   * The description for the opportunity.
   */
  @observable accessor description = ''

  /**
   * The legend name for the opportunity.
   */
  @observable accessor legendName = ''

  /**
   * The client user for the opportunity, if any.
   */
  @observable accessor clientUser: User | null = null

  /**
   * The client for the opportunity.
   */
  @computed
  get client() {
    return this.clientUser
      ? new OpportunityOfferClient(this.clientUser)
      : OpportunityOfferClient.none
  }

  /**
   * The job specs for the opportunity.
   */
  @observable accessor specs: string[] = []

  /**
   * The Provider Cut Amount for the opportunity.
   */
  @observable accessor providerCutAmount = 0

  /**
   * The date at which the opportunity was offered.
   */
  @observable accessor dateOffered = unixEpoch()

  /**
   * The date at which the opportunity expires.
   */
  @observable accessor dateExpires = unixEpoch()

  /**
   * The action to perform upon expiration.
   */
  @observable accessor expirationAction: OfferExpirationAction = 'Accept'

  /**
   * Whether the Offer was targeted to the Provider by request.
   */
  @observable accessor targeted = false

  /**
   *  The consultation associated with the offer, if any.
   */
  @observable accessor consultation: OpportunityOfferConsultation | null = null

  /**
   * Get the display name of the offer
   */
  @computed
  get displayName() {
    return this.name || this.description || this.legendName
  }

  /**
   * Countdown until expiration.
   */
  @computed
  get timeUntilExpiration() {
    return this._timeUntilExpiration()
  }

  /**
   * The date at which the offer expires, in milliseconds.
   */
  @computed
  get expirationMillis() {
    return this.dateExpires.getTime()
  }
}

/**
 * The offer client.
 */
export class OpportunityOfferClient {
  constructor(private _user: User) {}

  /**
   * Empty instance.
   */
  static get none() {
    return lazyNoneClient()
  }

  /**
   * The client's display name.
   */
  @computed
  get displayName() {
    return this._user.displayName
  }

  /**
   * The user.
   */
  @computed
  get user() {
    return this._user
  }
}

/**
 * An Opportunity Offer-representation of a Consultation.
 */
export interface OpportunityOfferConsultation {
  /**
   * The ID of the Consultation.
   */
  consultationId: Id

  /**
   * The bookable time slots, the client's availability split into slots of
   * the requested length.
   */
  availabilityTimes: TimeRange[]

  /**
   * The requested length for the Consultation, in minutes.
   */
  requestedLength: number
}

const lazyNoneClient = once(() => new OpportunityOfferClient(User.none))
